import React, { useCallback, useEffect, useState } from 'react'
import { Users, Loader2, AlertCircle, CheckCircle2, RefreshCw } from 'lucide-react'
import { crmSyncAPI } from '../services/api'

/**
 * Owner-only settings for the platform → house CRM mirror (vs-main-api
 * services/crm_mirror.py): every tenant appears as a client in VestirStudio's own
 * studio, tagged "VS: …", so the sales team can follow up from there.
 *
 * The house slug decides which studio receives every tenant's owner contacts, so a
 * change must be typed twice, and the API refuses a studio not on the
 * internal-staff tier. "Sincronizar agora" only queues a run for the next cron tick
 * (≤5 min) — the mirror never runs inside a request.
 */

const PROBLEM_LABEL = {
  not_found: 'Estúdio não encontrado.',
  inactive: 'O estúdio está inativo — a sincronização está pausada.',
  wrong_tier: 'O estúdio precisa estar no plano internal-staff — a sincronização está pausada.',
}

function apiErrorMessage(err, fallback = 'Algo deu errado. Tente novamente.') {
  const data = err?.response?.data
  if (data?.error && typeof data.error === 'object') return data.error.message || data.error.code || fallback
  if (typeof data?.error === 'string') return data.error
  return data?.message || err?.message || fallback
}

const formatDateTime = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR') : '—')

export const CrmSyncCard = () => {
  const [data, setData] = useState(null)
  const [slug, setSlug] = useState('')
  const [confirmSlug, setConfirmSlug] = useState('')
  const [productId, setProductId] = useState('')
  const [excluded, setExcluded] = useState('')
  const [saving, setSaving] = useState(false)
  const [running, setRunning] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const apply = (d) => {
    setData(d)
    setSlug(d.house_tenant_slug || '')
    setConfirmSlug('')
    setProductId(d.house_product_id || '')
    setExcluded((d.excluded_tenant_slugs || []).join(', '))
  }

  const load = useCallback(async () => {
    try {
      const res = await crmSyncAPI.get()
      apply(res.data.data)
    } catch (err) {
      setError(apiErrorMessage(err, 'Não foi possível carregar a sincronização com o CRM.'))
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const slugChanged = data && slug.trim().toLowerCase() !== (data.house_tenant_slug || '')

  const save = async () => {
    setError('')
    setNotice('')
    setSaving(true)
    try {
      const body = {
        house_tenant_slug: slug.trim().toLowerCase(),
        excluded_tenant_slugs: excluded.split(/[\s,]+/).filter(Boolean),
      }
      if (slugChanged) body.confirm_slug = confirmSlug
      // A product belongs to the old studio; drop it when the studio changes.
      body.house_product_id = slugChanged ? '' : productId
      const res = await crmSyncAPI.update(body)
      apply(res.data.data)
      setNotice('Configuração salva.')
    } catch (err) {
      setError(apiErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  const runNow = async () => {
    setError('')
    setNotice('')
    setRunning(true)
    try {
      const res = await crmSyncAPI.run()
      setNotice(
        res.data?.data?.queued
          ? 'Sincronização completa agendada — roda em até 5 minutos.'
          : 'Não foi possível agendar (Redis indisponível neste ambiente).'
      )
    } catch (err) {
      setError(apiErrorMessage(err))
    } finally {
      setRunning(false)
    }
  }

  const last = data?.last_run

  return (
    <div className="card p-6 mb-6" id="crm-sync-card">
      <div className="flex items-center gap-2 mb-1">
        <Users className="h-5 w-5 text-primary-600" />
        <h2 className="text-lg font-semibold text-admin-900">Sincronização com o CRM (estúdio interno)</h2>
      </div>
      <p className="text-sm text-admin-600 mb-5">
        Cada estúdio da plataforma aparece como cliente no nosso próprio estúdio, com etiquetas “VS: …” e o
        painel “Assinatura VestirStudio”. Mudanças chegam em até 5 minutos; uma revisão completa roda a cada
        hora. Nenhum dado dos clientes finais dos estúdios é copiado.
      </p>

      {error && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}
      {notice && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
          {notice}
        </div>
      )}

      {!data ? (
        <div className="text-sm text-admin-600">Carregando…</div>
      ) : (
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <label className="block">
              <span className="block text-sm font-medium text-admin-800 mb-1">Slug do estúdio interno</span>
              <input
                id="crm-house-slug"
                className="input"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="vitoria"
              />
              {data.house_problem && !slugChanged && (
                <span className="mt-1 block text-xs text-red-700">{PROBLEM_LABEL[data.house_problem]}</span>
              )}
            </label>
            {slugChanged && slug.trim() && (
              <label className="block">
                <span className="block text-sm font-medium text-admin-800 mb-1">
                  Digite o slug novamente para confirmar
                </span>
                <input
                  id="crm-house-slug-confirm"
                  className="input"
                  value={confirmSlug}
                  onChange={(e) => setConfirmSlug(e.target.value)}
                />
                <span className="mt-1 block text-xs text-admin-600">
                  Este estúdio receberá os contatos de todos os proprietários de estúdios.
                </span>
              </label>
            )}
            {!slugChanged && (
              <label className="block">
                <span className="block text-sm font-medium text-admin-800 mb-1">Produto da assinatura</span>
                <select
                  id="crm-house-product"
                  className="input"
                  value={productId}
                  onChange={(e) => setProductId(e.target.value)}
                  disabled={!data.products.length}
                >
                  <option value="">Nenhum (só dados e etiquetas)</option>
                  {data.products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
                <span className="mt-1 block text-xs text-admin-600">
                  Atribuído uma vez, sem tarefas e sem preço, a quem tem acesso; entra na primeira etapa do pipeline.
                </span>
              </label>
            )}
          </div>

          <label className="block">
            <span className="block text-sm font-medium text-admin-800 mb-1">Estúdios a ignorar (slugs)</span>
            <input
              id="crm-excluded-slugs"
              className="input"
              value={excluded}
              onChange={(e) => setExcluded(e.target.value)}
              placeholder="jimenaalzamora, demo, teste"
            />
          </label>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-admin-200 pt-4">
            <div className="text-xs text-admin-600">
              {data.linked_tenants} estúdio(s) vinculados · última execução: {formatDateTime(last?.ran_at)}
              {last && ` (${last.mode}: ${last.updated ?? 0} atualizados, ${last.created ?? 0} novos, ${last.errors ?? 0} erros)`}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={runNow}
                disabled={running || !data.house_ok}
              >
                {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Sincronizar agora
              </button>
              <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CrmSyncCard
